<script setup lang="ts">
/* =============================================================
   UPCOMING — ONE BERTH'S NEXT EVENTS
   =============================================================
   Blocked time and live bookings in one column, earliest first,
   each behind a date tile so the eye can run down the dates
   without reading. The page decides what goes in; this only
   lays it out.

   Not an <ItemRow>: the date tile is the point of the screen,
   and ItemRow has no place for one.
   ============================================================= */

import { RouterLink } from 'vue-router'
import Badge from '@/components/ui/Badge.vue'
import Icon from '@/components/Icon.vue'

export interface UpcomingEntry {
  key: string
  kind: 'block' | 'booking'
  /** ISO instants. */
  start: string
  end: string
  title: string
  /** Second line: the time span, the vessel. */
  detail: string
  badge: string
  tone: 'neutral' | 'accent' | 'success' | 'warning' | 'danger'
  to?: string
}

defineProps<{ entries: UpcomingEntry[] }>()

const day = new Intl.DateTimeFormat('en-GB', { day: 'numeric' })
const month = new Intl.DateTimeFormat('en-GB', { month: 'short' })

/** Started already: the tile says "now" rather than a date in the past. */
const ongoing = (e: UpcomingEntry) => new Date(e.start).getTime() <= Date.now()
</script>

<template>
  <ol class="list">
    <li v-for="e in entries" :key="e.key" class="item" :class="e.kind">
      <component :is="e.to ? RouterLink : 'div'" :to="e.to" class="row" :class="{ link: e.to }">
        <span class="date" aria-hidden="true">
          <template v-if="ongoing(e)">
            <span class="now">Now</span>
          </template>
          <template v-else>
            <span class="day">{{ day.format(new Date(e.start)) }}</span>
            <span class="month">{{ month.format(new Date(e.start)) }}</span>
          </template>
        </span>
        <span class="text">
          <span class="top">
            <span class="title">{{ e.title }}</span>
            <Badge :tone="e.tone">{{ e.badge }}</Badge>
          </span>
          <span class="detail">{{ e.detail }}</span>
        </span>
        <Icon v-if="e.to" name="chevronRight" :size="16" class="chevron" />
      </component>
    </li>
  </ol>
</template>

<style scoped>
.list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.item {
  border-bottom: var(--border-width) solid var(--color-border);
}

.item:last-child {
  border-bottom: 0;
}

.row {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  font-size: var(--text-sm);
  color: inherit;
  text-decoration: none;
}

.link:hover {
  background-color: var(--color-surface-hover);
}

.link:hover .title {
  color: var(--color-accent);
}

.date {
  flex: none;
  display: grid;
  place-items: center;
  align-content: center;
  width: 3rem;
  height: 3rem;
  line-height: 1.1;
  border-radius: var(--radius-sm);
  background-color: var(--color-accent-soft);
  color: var(--color-accent-hover);
}

.block .date {
  background-color: var(--color-surface-sunken);
  color: var(--color-text-muted);
  background-image: repeating-linear-gradient(
    -45deg,
    transparent 0 5px,
    color-mix(in srgb, var(--color-border) 70%, transparent) 5px 7px
  );
}

.day {
  font-size: var(--text-md);
  font-weight: var(--weight-bold);
  font-variant-numeric: tabular-nums;
}

.month,
.now {
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
}

.text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3xs) var(--space-xs);
}

.title {
  min-width: 0;
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

.detail {
  color: var(--color-text-muted);
}

.chevron {
  flex: none;
  color: var(--color-text-faint);
}
</style>
